import { Analytics02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Tooltip } from '@pique/component';
import { rightSidebarWidthAtom } from '@pique/core/atoms';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';

import {
  actionItemsAssigneeModeAtom,
  actionItemsHeaderModeAtom,
} from '../../../../atoms';
import { ActionItemsPanel } from '../../../../pages/workspace/detail-page/tabs/pique-action-items-list/ActionItemsPanel';
import type { ViewMode } from '../../../../pages/workspace/detail-page/tabs/pique-action-items-list/ViewModeSelector';
import { AuthService } from '../../../cloud';
import type { SidebarTab } from '../../entities/sidebar-tab';
import { ViewService } from '../../services/view';
import { WorkbenchService } from '../../services/workbench';
import { ViewSidebarTabBodyTarget } from '../view-islands';
import * as styles from './sidebar-container.css';
import { Header } from './sidebar-header';
import { SidebarHeaderSwitcher } from './sidebar-header-switcher';

const LazyViewModeSelector = lazy(() =>
  import(
    '../../../../pages/workspace/detail-page/tabs/pique-action-items-list/ViewModeSelector'
  ).then(module => ({ default: module.ViewModeSelector }))
);

const ViewModeSelectorLoading = () => (
  <div
    style={{
      width: 32,
      height: 24,
      borderRadius: 6,
      backgroundColor: 'var(--affine-border-color)',
      opacity: 0.25,
    }}
    data-tauri-drag-region="false"
  />
);

const ActionItemsHeaderWithStats: React.FC<{
  userLabel: string;
  canShowMine: boolean;
  mode: 'me' | 'all';
  onModeChange: (mode: 'me' | 'all') => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  focusModeEnabled?: boolean;
  onFocusToggle?: () => void;
  focusActive?: boolean;
}> = ({
  userLabel,
  canShowMine,
  mode,
  onModeChange,
  viewMode,
  onViewModeChange,
  focusModeEnabled: _focusModeEnabled,
  onFocusToggle: _onFocusToggle,
  focusActive: _focusActive,
}) => {
  const handleSelect = useCallback(
    (nextMode: 'me' | 'all') => (e: React.MouseEvent<HTMLButtonElement>) => {
      onModeChange(nextMode);
      e.currentTarget.blur();
    },
    [onModeChange]
  );

  return (
    <div className={styles.actionItemsHeaderContainer} data-tauri-drag-region>
      <div
        className={styles.actionItemsHeader}
        data-testid="action-items-header"
        data-tauri-drag-region
      >
        {canShowMine ? (
          <>
            <div
              className={styles.assigneeToggle}
              aria-label="Assignee filter"
              data-tauri-drag-region="false"
            >
              <button
                type="button"
                className={styles.assigneeOption}
                data-selected={mode === 'me'}
                onClick={handleSelect('me')}
                title={`Only ${userLabel}'s items`}
                data-tauri-drag-region="false"
              >
                {userLabel}
              </button>
              <button
                type="button"
                className={styles.assigneeOption}
                data-selected={mode === 'all'}
                data-role="all"
                onClick={handleSelect('all')}
                title="All action items"
                data-tauri-drag-region="false"
              >
                All
              </button>
            </div>
            <span className={styles.actionItemsHeaderTitle}>Action Items</span>
          </>
        ) : (
          <span className={styles.actionItemsHeaderTitle}>Action Items</span>
        )}

        <div
          className={styles.actionItemsHeaderControls}
          data-tauri-drag-region="false"
        >
          <Suspense fallback={<ViewModeSelectorLoading />}>
            <LazyViewModeSelector
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
            />
          </Suspense>
        </div>
      </div>
      <div className={styles.statsContainer}></div>
    </div>
  );
};

export const SidebarContainer = ({
  className,
  ...props
}: React.HtmlHTMLAttributes<HTMLDivElement>) => {
  const workbenchService = useService(WorkbenchService);
  const workbench = workbenchService.workbench;
  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);
  const viewService = useService(ViewService);
  const view = viewService.view;
  const sidebarTabs = useLiveData(view.sidebarTabs$);
  const activeSidebarTab = useLiveData(view.activeSidebarTab$);

  const userFirstName = account?.label?.split(' ')[0];
  const userEmail = account?.email;
  const userLabel = userFirstName ? `${userFirstName}'s` : 'My';

  const [assigneeMode, setAssigneeMode] = useAtom(actionItemsAssigneeModeAtom);
  const [headerMode, setHeaderMode] = useAtom(actionItemsHeaderModeAtom);
  const [viewMode, setViewMode] = useState<ViewMode>('pageId');
  const [sidebarWidth, setSidebarWidth] = useAtom(rightSidebarWidthAtom);
  const prevWidthRef = useRef(sidebarWidth);

  useEffect(() => {
    if (viewMode === 'kanban') {
      prevWidthRef.current = sidebarWidth;
      const halfScreen = Math.round(window.innerWidth * 0.5);
      setSidebarWidth(Math.max(halfScreen, sidebarWidth));
    } else {
      if (prevWidthRef.current > 0) {
        setSidebarWidth(prevWidthRef.current);
      }
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChartToggle = useCallback(() => {
    setHeaderMode(prev => {
      const next = prev === 'chart' ? 'addItem' : 'chart';
      if (next === 'chart') {
        window.dispatchEvent(new Event('pique-action-items-progress-toggle'));
      }
      return next;
    });
  }, [setHeaderMode]);

  const assignedToEmails =
    assigneeMode === 'me' && userEmail ? [userEmail] : undefined;

  const handleToggleOpen = useCallback(() => {
    workbench.toggleSidebar();
  }, [workbench]);

  return (
    <div className={clsx(styles.sidebarContainerInner, className)} {...props}>
      <Header
        floating={false}
        onToggle={handleToggleOpen}
        rightControls={
          sidebarTabs.length === 0 ? (
            <Tooltip
              content={
                headerMode === 'chart'
                  ? 'Hide progress chart'
                  : 'Show progress chart'
              }
              side="bottom"
            >
              <button
                type="button"
                className={styles.chartToggleButton}
                data-active={headerMode === 'chart'}
                data-testid="action-items-progress-toggle"
                onClick={handleChartToggle}
                aria-label="Toggle progress chart"
                data-tauri-drag-region="false"
              >
                <HugeiconsIcon
                  icon={Analytics02Icon}
                  size={14}
                  strokeWidth={1.5}
                />
              </button>
            </Tooltip>
          ) : undefined
        }
      >
        {sidebarTabs.length > 0 ? (
          <SidebarHeaderSwitcher />
        ) : (
          <ActionItemsHeaderWithStats
            userLabel={userLabel}
            canShowMine={Boolean(userEmail)}
            mode={assigneeMode}
            onModeChange={setAssigneeMode}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        )}
      </Header>
      {sidebarTabs.length > 0 ? (
        sidebarTabs.map((sidebar: SidebarTab) => (
          <ViewSidebarTabBodyTarget
            tabId={sidebar.id}
            key={sidebar.id}
            style={{ display: activeSidebarTab === sidebar ? 'block' : 'none' }}
            viewId={view.id}
            className={clsx(styles.sidebarBodyTarget, styles.borderTop)}
          />
        ))
      ) : (
        <div className={clsx(styles.sidebarBodyTarget, styles.borderTop)}>
          <ActionItemsPanel
            assignedToEmails={assignedToEmails}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            showViewModeSelector={false}
          />
        </div>
      )}
    </div>
  );
};
